import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";
import type { QueryClient } from "@tanstack/react-query";
import { createStore } from "zustand/vanilla";

/**
 * Answers that concern the whole app rather than one screen, caught from
 * every query and mutation: `UPGRADE_REQUIRED` (the Android build is too
 * old, P6-T13: the app shows the update screen) and a quick switch that
 * ended (`switch_ended`, P6-T11: the device goes back to the signed-in
 * person).
 */
export type AppGateState = {
	/** Set once the API asked for a newer app: where to get it. */
	upgrade: { downloadUrl: string | null } | null;
	clearUpgrade(): void;
};

export function createAppGateStore() {
	return createStore<AppGateState>((set) => ({
		upgrade: null,
		clearUpgrade: () => set({ upgrade: null }),
	}));
}

export type AppGateStore = ReturnType<typeof createAppGateStore>;

/** Watches the query and mutation caches for those answers. */
export function watchAppGate(
	queryClient: QueryClient,
	gate: AppGateStore,
	onSwitchEnded: () => void,
): () => void {
	const check = (error: unknown) => {
		const code = errorCodeOf(error);
		if (code === "UPGRADE_REQUIRED") {
			const url = errorDetailsOf(error).downloadUrl;
			gate.setState({ upgrade: { downloadUrl: typeof url === "string" ? url : null } });
		} else if (code === "FORBIDDEN" && errorDetailsOf(error).reason === "switch_ended") {
			onSwitchEnded();
		}
	};
	const offQueries = queryClient.getQueryCache().subscribe((event) => {
		if (event.type === "updated" && event.action.type === "error") check(event.action.error);
	});
	const offMutations = queryClient.getMutationCache().subscribe((event) => {
		if (event.type === "updated" && event.action.type === "error") check(event.action.error);
	});
	return () => {
		offQueries();
		offMutations();
	};
}
